import React from "react";
import { format, parseISO } from "date-fns";

import Box from "@material-ui/core/Box";
import CircularProgress from "@material-ui/core/CircularProgress";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";

import type { SupportErrorAlertTimeline } from "src/interfaces/supportErrorAlerts.interface";

const HOUR_MS = 60 * 60 * 1000;
const MAX_AXIS_LABELS = 7;

function formatBucketLabel(start: string, bucketMs: number) {
  const date = parseISO(start);
  if (bucketMs <= HOUR_MS) return format(date, "ha");
  if (bucketMs <= 6 * HOUR_MS) return format(date, "MMM d, ha");
  if (bucketMs <= 24 * HOUR_MS) return format(date, "MMM d");
  return format(date, "MMM d");
}

function formatBucketTooltip(start: string, bucketMs: number) {
  const date = parseISO(start);
  if (bucketMs <= 6 * HOUR_MS) return format(date, "MMM d, yyyy h:mm a");
  return format(date, "MMM d, yyyy");
}

function getAxisLabelIndices(bucketCount: number) {
  if (bucketCount <= MAX_AXIS_LABELS) {
    return new Set(Array.from({ length: bucketCount }, (_, index) => index));
  }

  const indices = new Set<number>();
  const step = (bucketCount - 1) / (MAX_AXIS_LABELS - 1);
  for (let index = 0; index < MAX_AXIS_LABELS; index += 1) {
    indices.add(Math.round(index * step));
  }
  return indices;
}

const useStyles = makeStyles((theme) => ({
  root: {
    marginBottom: theme.spacing(2),
  },
  chart: {
    display: "flex",
    alignItems: "flex-end",
    gap: theme.spacing(0.5),
    height: 72,
    marginTop: theme.spacing(1),
  },
  axis: {
    display: "flex",
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(0.5),
  },
  axisCell: {
    flex: 1,
    minWidth: 4,
    overflow: "hidden",
  },
  axisLabel: {
    display: "block",
    fontSize: 10,
    lineHeight: 1.2,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  bar: {
    flex: 1,
    minWidth: 4,
    borderRadius: theme.shape.borderRadius,
    cursor: "pointer",
    transition: "background-color 0.15s ease",
  },
  barEmpty: {
    backgroundColor: theme.palette.grey[300],
    minHeight: 2,
  },
  barFilled: {
    backgroundColor: theme.palette.primary.light,
    minHeight: 8,
  },
  barSelected: {
    backgroundColor: theme.palette.primary.main,
  },
  loading: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    minHeight: 72,
  },
}));

interface ErrorAlertsTimelineProps {
  timeline?: SupportErrorAlertTimeline;
  loading?: boolean;
  selectedBucketStart?: string | null;
  onSelectBucket: (from: Date, to: Date) => void;
}

function ErrorAlertsTimeline({
  timeline,
  loading = false,
  selectedBucketStart = null,
  onSelectBucket,
}: ErrorAlertsTimelineProps) {
  const classes = useStyles();

  if (loading && !timeline) {
    return (
      <Box className={classes.loading}>
        <CircularProgress size={20} />
        <Typography variant="body2" color="textSecondary">
          Loading timeline…
        </Typography>
      </Box>
    );
  }

  if (!timeline?.buckets.length) return null;

  const maxCount = Math.max(
    ...timeline.buckets.map((bucket) => bucket.count),
    1
  );
  const axisLabelIndices = getAxisLabelIndices(timeline.buckets.length);

  return (
    <Box className={classes.root}>
      <Typography variant="caption" color="textSecondary">
        Alert volume — click a segment to narrow the date range
      </Typography>
      <Box className={classes.chart}>
        {timeline.buckets.map((bucket) => {
          const heightPercent = bucket.count
            ? Math.max((bucket.count / maxCount) * 100, 12)
            : 2;
          const isSelected = selectedBucketStart === bucket.start;

          return (
            <Tooltip
              key={bucket.start}
              title={`${formatBucketTooltip(
                bucket.start,
                timeline.bucketMs
              )} · ${bucket.count} alert${bucket.count === 1 ? "" : "s"}`}
            >
              <Box
                className={[
                  classes.bar,
                  bucket.count ? classes.barFilled : classes.barEmpty,
                  isSelected ? classes.barSelected : "",
                ].join(" ")}
                style={{ height: `${heightPercent}%` }}
                onClick={() =>
                  onSelectBucket(parseISO(bucket.start), parseISO(bucket.end))
                }
              />
            </Tooltip>
          );
        })}
      </Box>
      <Box className={classes.axis}>
        {timeline.buckets.map((bucket, index) => (
          <Box key={`${bucket.start}-axis`} className={classes.axisCell}>
            {axisLabelIndices.has(index) ? (
              <Typography
                variant="caption"
                color="textSecondary"
                className={classes.axisLabel}
              >
                {formatBucketLabel(bucket.start, timeline.bucketMs)}
              </Typography>
            ) : null}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export default ErrorAlertsTimeline;
